// src/utils/readiness.js
// =====================================================================
// MOTEUR READINESS COMMUN (stratégie « infrastructure patrimoniale », §9.5)
//   ARTWORK DATA → Art Asset Readiness → Collection Readiness → Portfolio Readiness
// UN seul moteur, calculé depuis les données de l'œuvre, exposé différemment
// selon le rôle/les permissions. Ne PAS le redévelopper par dashboard.
// =====================================================================

import i18next from "i18next";

export const VALUATION_STALE_MONTHS = 24;

const tr = (key, fallback, options = {}) =>
  i18next.t(key, { defaultValue: fallback, ...options });

// ---- helpers de lecture tolérants (données hétérogènes / imports) ----
const nonEmpty = (v) => v !== null && v !== undefined && String(v).trim() !== "" && String(v).trim() !== "[]";

function hasImages(a) {
  if (a?.image_url && String(a.image_url).trim()) return true;
  const imgs = a?.images;
  if (Array.isArray(imgs)) return imgs.length > 0;
  if (typeof imgs === "string") { try { const p = JSON.parse(imgs); return Array.isArray(p) && p.length > 0; } catch { return imgs.trim() !== "" && imgs.trim() !== "[]"; } }
  return false;
}

function hasList(v) {
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "string") { try { const p = JSON.parse(v); return Array.isArray(p) ? p.length > 0 : nonEmpty(v); } catch { return nonEmpty(v); } }
  return false;
}

function hasDimensions(a) {
  return Boolean(a?.dimensions_height || a?.dimensions_width || a?.height || a?.width || nonEmpty(a?.dimensions));
}

function hasMedium(a) {
  return nonEmpty(a?.medium_normalized_key) || nonEmpty(a?.medium) || nonEmpty(a?.medium_original);
}

function hasValue(a) {
  return [a?.estimated_value, a?.value, a?.current_estimated_value, a?.valuation_estimated_value, a?.valuation_consensus]
    .some((v) => v !== null && v !== undefined && Number(v) > 0);
}

function monthsSince(dateLike) {
  if (!dateLike) return Infinity;
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return Infinity;
  return (Date.now() - d.getTime()) / (1000 * 3600 * 24 * 30.4375);
}

function lastValuationMonths(a) {
  return Math.min(
    monthsSince(a?.last_valuation_date),
    monthsSince(a?.expert_date),
    monthsSince(a?.last_estimation_date)
  );
}

function hasCertificate(a) {
  return a?.certificate_auth === true || nonEmpty(a?.certificate_authenticity);
}

function isInsured(a) {
  const s = String(a?.insurance_status || "").toLowerCase();
  return ["insured", "active", "covered", "assure", "assuré", "couvert"].includes(s);
}

function transmissionPrepared(a) {
  const s = String(a?.transmission_status || "").toLowerCase();
  if (["planned", "prepared", "planifie", "planifié", "prepare", "préparé", "done", "completed"].includes(s)) return true;
  return Boolean(a?.bare_owner_id || a?.usufruct_beneficiary_id);
}

const dimensionLabel = (key, fallback) => tr(`readiness.dimensions.${key}`, fallback);

// ---- Dimensions (§7 : 8 dimensions). weight = importance relative ----
// label reste disponible pour compatibilité avec les composants existants,
// mais devient dynamique selon la langue active i18next.
export const READINESS_DIMENSIONS = [
  { key: "identification", get label() { return dimensionLabel("identification", "Identification"); }, weight: 1, test: (a) => nonEmpty(a?.title) && nonEmpty(a?.artist) && hasMedium(a) && hasDimensions(a) && hasImages(a) },
  { key: "documentation", get label() { return dimensionLabel("documentation", "Documentation"); }, weight: 1, test: (a) => a?.invoice === true || hasList(a?.documents) || hasCertificate(a) },
  { key: "provenance", get label() { return dimensionLabel("provenance", "Authenticité / provenance"); }, weight: 1, test: (a) => nonEmpty(a?.provenance) || hasCertificate(a) },
  { key: "valuation", get label() { return dimensionLabel("valuation", "Valorisation à jour"); }, weight: 1.5, test: (a) => hasValue(a) && lastValuationMonths(a) <= VALUATION_STALE_MONTHS },
  { key: "insurance", get label() { return dimensionLabel("insurance", "Assurance"); }, weight: 1, test: (a) => isInsured(a) },
  { key: "conservation", get label() { return dimensionLabel("conservation", "Conservation suivie"); }, weight: 1, test: (a) => nonEmpty(a?.condition) },
  { key: "property", get label() { return dimensionLabel("property", "Propriété clarifiée"); }, weight: 1, test: (a) => nonEmpty(a?.ownership_status) },
  { key: "transmission", get label() { return dimensionLabel("transmission", "Transmission préparée"); }, weight: 1, test: (a) => transmissionPrepared(a) },
];

const TOTAL_WEIGHT = READINESS_DIMENSIONS.reduce((s, d) => s + d.weight, 0);

// ---- Art Asset Readiness : score d'une œuvre (0-100) + détail dimensions ----
export function computeArtworkReadiness(artwork) {
  const dimensions = {};
  let weighted = 0;
  for (const d of READINESS_DIMENSIONS) {
    const ok = Boolean(d.test(artwork));
    dimensions[d.key] = ok;
    if (ok) weighted += d.weight;
  }
  return { dimensions, score: Math.round((weighted / TOTAL_WEIGHT) * 100) };
}

const actionLabel = (key, count, fallback) =>
  tr(`readiness.actions.${key}`, fallback, { count });

// ---- libellés d'action par dimension (impératif, avec le nombre concerné) ----
const ACTION_LABEL = {
  identification: (n) => actionLabel("identification", n, `Compléter l'identification de ${n} œuvre(s)`),
  documentation: (n) => actionLabel("documentation", n, `Ajouter facture / certificat pour ${n} œuvre(s)`),
  provenance: (n) => actionLabel("provenance", n, `Documenter la provenance de ${n} œuvre(s)`),
  valuation: (n) => actionLabel("valuation", n, `Actualiser la valorisation de ${n} œuvre(s)`),
  insurance: (n) => actionLabel("insurance", n, `Vérifier l'assurance de ${n} œuvre(s)`),
  conservation: (n) => actionLabel("conservation", n, `Renseigner l'état de conservation de ${n} œuvre(s)`),
  property: (n) => actionLabel("property", n, `Clarifier le statut de propriété de ${n} œuvre(s)`),
  transmission: (n) => actionLabel("transmission", n, `Préparer la transmission de ${n} œuvre(s)`),
};

// ---- Hiérarchie des alertes (§P2 : critique / recommandée / information) ----
// Logique patrimoniale : un défaut d'assurance ou de transmission est CRITIQUE (risque) ;
// documentation / provenance / valorisation / propriété = RECOMMANDÉ ; le reste = information.
const DIMENSION_PRIORITY = {
  insurance: "critical",
  transmission: "critical",
  valuation: "recommended",
  documentation: "recommended",
  provenance: "recommended",
  property: "recommended",
  identification: "info",
  conservation: "info",
};
const PRIORITY_RANK = { critical: 0, recommended: 1, info: 2 };

// ---- Collection Readiness : agrégat sur un ensemble d'œuvres ----
export function computeCollectionReadiness(artworks = []) {
  const total = artworks.length;
  const dims = READINESS_DIMENSIONS.map((d) => {
    let ok = 0;
    for (const a of artworks) if (d.test(a)) ok += 1;
    const missing = total - ok;
    const pct = total ? Math.round((ok / total) * 100) : 0;
    return { key: d.key, label: d.label, weight: d.weight, ok, missing, pct };
  });

  const overall = total
    ? Math.round(dims.reduce((s, d) => s + d.pct * (d.weight || 1), 0) / dims.reduce((s, d) => s + (d.weight || 1), 0))
    : 0;

  const actions = dims
    .filter((d) => d.missing > 0)
    .map((d) => ({
      key: d.key,
      count: d.missing,
      priority: DIMENSION_PRIORITY[d.key] || "info",
      label: (ACTION_LABEL[d.key] || ((n) => tr("readiness.actions.generic", `${n} œuvre(s)`, { count: n })))(d.missing),
    }))
    // Tri : critique d'abord, puis nombre d'œuvres concernées décroissant.
    .sort((a, b) => (PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]) || (b.count - a.count));

  const exceptionalUnprepared = artworks.filter((a) => a?.is_exceptional === true && !transmissionPrepared(a)).length;

  return { total, dimensions: dims, overall, actions, exceptionalUnprepared };
}

// Portfolio Readiness (§9.3) : agrège par client (famille) + consolidé global.
export function computePortfolioReadiness(artworks = []) {
  const byClient = new Map();
  for (const a of artworks) {
    const k = a?.client_id || "∅";
    if (!byClient.has(k)) byClient.set(k, []);
    byClient.get(k).push(a);
  }
  const perClient = {};
  for (const [k, rows] of byClient.entries()) perClient[k] = computeCollectionReadiness(rows);
  return { perClient, overall: computeCollectionReadiness(artworks).overall, total: artworks.length };
}

// Priorité d'attention dérivée du score (pour tables pro FO/Advisor).
export function readinessPriority(pct) {
  if (pct < 60) return { key: "high", label: tr("readiness.priority.high", "Élevée") };
  if (pct < 80) return { key: "medium", label: tr("readiness.priority.medium", "Moyenne") };
  return { key: "low", label: tr("readiness.priority.low", "Faible") };
}

// Colonnes minimales à charger pour alimenter le moteur (SELECT Supabase).
export const READINESS_SELECT =
  "id,client_id,title,artist,medium,medium_original,medium_normalized_key,dimensions,dimensions_height,dimensions_width,height,width," +
  "image_url,images,invoice,documents,certificate_auth,certificate_authenticity,provenance," +
  "estimated_value,value,current_estimated_value,valuation_estimated_value,valuation_consensus," +
  "last_valuation_date,expert_date,last_estimation_date,insurance_status,condition,ownership_status," +
  "transmission_status,bare_owner_id,usufruct_beneficiary_id,is_exceptional";

export const READINESS_SELECT_SECURE = READINESS_SELECT.replace(",invoice", "");
